import CloseIcon from './CloseIcon.jsx';
import {tierColor as passTierColor} from '../utils/passTiers.js';
import {displayColor} from '../utils/displayColor.js';
import { useState, useEffect } from 'react';
import { CATEGORY_COLORS } from '../utils/visuals.js';
import { tierFor } from '../hooks/useTierC.js';
import ISSDetailPanel from './ISSDetailPanel.jsx';
import LaunchCard from './LaunchCard.jsx';
import BottomSheet from './BottomSheet.jsx';
import AISummary from './AISummary.jsx';
import SatelliteVideo from './SatelliteVideo.jsx';
import {LIVE_STREAMS} from '../utils/satelliteVideos.js';
import { useIsMobile } from '../hooks/useIsMobile.js';

const API = import.meta.env.VITE_API_BASE || '/api';
const TIER_ICONS  = ['', '👁', '⭐', '📷'];
const TIER_LABELS = ['Not visible', 'Naked-eye visible', 'Worth going outside', 'Photograph this'];

const s = {
  card: {
    width: 270,
    background: 'var(--bg-2)', border: '1px solid var(--hair-2)',
    borderRadius: 10, padding: '14px 16px', backdropFilter: 'blur(8px)',
    fontFamily: 'var(--mono)', color: 'var(--txt)', userSelect: 'none',
    maxHeight: 'calc(100vh - 140px)', overflowY: 'auto',
  },
  cardCompact: {
    width: '100%',
    background: 'transparent', border: 'none',
    borderRadius: 0, padding: '10px 10px',
    fontFamily: 'var(--mono)', color: 'var(--txt)', userSelect: 'none',
    overflowY: 'auto',
  },
  name:  { fontSize: 16, fontWeight: 900, color: 'var(--accent)', letterSpacing: 0.5, marginBottom: 8 },
  row:   { display: 'flex', justifyContent: 'space-between', fontSize: 'var(--ui-body)', marginBottom: 4 },
  label: { color: 'var(--txt-dim)', fontWeight: 500 },
  val:   { color: 'var(--txt)', fontWeight: 600 },
  chip: (cat) => ({
    display: 'inline-block', padding: '3px 10px', borderRadius: 99,
    fontSize: 'var(--ui-label)', fontWeight: 700, marginBottom: 10,
    background: (displayColor(CATEGORY_COLORS[cat])) + '22',
    color: displayColor(CATEGORY_COLORS[cat]),
    border: `1px solid ${displayColor(CATEGORY_COLORS[cat])}55`,
  }),
  sunChip: (lit) => ({
    display: 'inline-block', padding: '3px 10px', borderRadius: 99,
    fontSize: 'var(--ui-label)', fontWeight: 700, marginBottom: 10, marginLeft: 6,
    background: lit ? 'var(--amber-2)' : 'var(--hair-2)',
    color: lit ? 'var(--accent)' : 'var(--txt-dim)',
    border: `1px solid ${lit ? 'var(--accent)' : 'var(--txt-dim)'}`,
  }),
  divider: { borderColor: 'var(--hair-2)', margin: '10px 0' },
  tierRow: (color) => ({
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '7px 10px', borderRadius: 6, marginTop: 8,
    background: color + '15', border: `1px solid ${color}33`,
  }),
  tierLabel: (color) => ({ fontSize: 'var(--ui-body)', fontWeight: 700, color }),
  passSection: { marginTop: 8 },
  passItem: {
    padding: '6px 0', borderBottom: '1px solid var(--hair)',
    fontSize: 'var(--ui-body)', color: 'var(--txt-dim)', lineHeight: 1.7,
  },
  passTime: { color: 'var(--txt)', fontWeight: 600 },
  btns: { display: 'flex', gap: 8, marginTop: 12 },
  btn: (active) => ({
    flex: 1, padding: '7px 0', borderRadius: 6, fontSize: 'var(--ui-body)',
    fontWeight: 700, cursor: 'pointer', border: '1px solid',
    borderColor: active ? 'var(--accent)' : 'var(--hair-2)',
    background: active ? 'var(--amber-2)' : 'transparent',
    color: active ? 'var(--accent)' : 'var(--txt-dim)',
    fontFamily: 'var(--mono)', letterSpacing: '0.04em',
  }),
  close: {
    position: 'absolute', top: 10, right: 12, background: 'none',
    border: 'none', color: 'var(--txt-dim)', cursor: 'pointer', fontSize: 18, lineHeight: 1,
  },
};

function formatTime(iso) {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
function formatCountdown(iso) {
  const diff = new Date(iso) - Date.now();
  if (diff < 0) return 'Now';
  const m = Math.floor(diff / 60000);
  const sec = Math.floor((diff % 60000) / 1000);
  if (m > 60) return `in ${Math.floor(m/60)}h ${m%60}m`;
  return m > 0 ? `in ${m}m ${sec}s` : `in ${sec}s`;
}
function fmtDuration(sec) {
  if (sec == null) return '-';
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60 ? `${sec % 60}s` : ''}`.trim();
  return `${Math.floor(sec / 3600)}h ${Math.round((sec % 3600) / 60)}m`;
}

function ShareButton({ norad, name }) {
  const [copied, setCopied] = useState(false);

  function share() {
    const url = `${window.location.origin}${window.location.pathname}?norad=${norad}`;
    if (navigator.share) {
      navigator.share({ title: `OrbitalGlass, ${name}`, url })
        .catch(() => {});
    } else {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  }

  return (
    <button
      onClick={share}
      style={{
        padding: '7px 12px', borderRadius: 6, fontSize: 'var(--ui-body)',
        fontWeight: 700, cursor: 'pointer', border: '1px solid',
        borderColor: copied ? 'var(--green)' : 'var(--hair-2)',
        background: copied ? 'var(--amber-2)' : 'transparent',
        color: copied ? 'var(--green)' : 'var(--txt-dim)',
        fontFamily: 'var(--mono)', transition: 'all 0.2s',
        whiteSpace: 'nowrap', letterSpacing: '0.04em',
      }}
    >
      {copied ? '✓ COPIED' : '⎘ SHARE'}
    </button>
  );
}

export default function SatelliteCard({ satellite: sat, tracked, observer, onFollow, onUntrack, onClose, compact, inline = false }) {
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = useState(true);
  const [nextPass,  setNextPass]  = useState(null);
  const [passLoading, setPassLoading] = useState(false);
  const [detail,    setDetail]    = useState(null);  // SATCAT enrichment

  // Reset sheet open state when satellite changes
  useEffect(() => { setSheetOpen(true); }, [sat?.norad_id]);

  function handleClose() {
    if (isMobile) {
      setSheetOpen(false);
      // Small delay so sheet can visually dismiss before unmounting
      setTimeout(onClose, 50);
    } else {
      onClose();
    }
  }

  // Fetch SATCAT detail, not for aviation/ground stations/launches
  useEffect(() => {
    const skip = ['aviation', 'groundstation', 'launches'];
    if (!sat?.norad_id || skip.includes(sat.category)) {
      setDetail(null); return;
    }
    let cancelled = false;
    fetch(`${API}/satellites/${sat.norad_id}/detail`)
      .then(r => r.json())
      .then(d => { if (!cancelled) setDetail(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [sat?.norad_id, sat?.category]);

  useEffect(() => {
    const isOrbit = sat?.category && !['aviation','groundstation','launches'].includes(sat.category);
    if (!observer || !sat || !isOrbit) { setNextPass(null); return; }
    let cancelled = false;
    setPassLoading(true);
    fetch(`${API}/satellites/${sat.norad_id}/passes?lat=${observer.lat}&lon=${observer.lon}&days=3`)
      .then(r => r.json())
      .then(d => { if (!cancelled) { setNextPass(d.passes?.[0] || null); setPassLoading(false); } })
      .catch(() => { if (!cancelled) setPassLoading(false); });
    return () => { cancelled = true; };
  }, [sat?.norad_id, observer?.lat, observer?.lon]);

  if (!sat) return null;

  const tier = nextPass?.visibility_tier ?? null;
  const tierColor = tier !== null ? passTierColor(tier) : null;

  const cardContent = (
    <>
      {!isMobile && <button title="Close" aria-label="Close satellite details" className="og-close-control" style={s.close} onClick={handleClose}><CloseIcon /></button>}
      <div style={s.name}>{sat.name}</div>
      <span style={s.chip(sat.category)}>{sat.category?.toUpperCase()}</span>
      {!['groundstation','aviation','launches'].includes(sat.category) && (
        <span style={s.sunChip(sat.sun_illuminated)}>
          {sat.sun_illuminated ? '☀ Sunlit' : '● Shadow'}
        </span>
      )}

      <SatelliteVideo key={sat.norad_id} streams={LIVE_STREAMS[sat.norad_id]} />

      {/* Launch site card */}
      {sat.category === 'launches' && sat.launch && (
        <LaunchCard launch={sat.launch} />
      )}

      {/* AI summary, orbital objects only */}
      {!['aviation','groundstation','launches'].includes(sat.category) && (
        <AISummary sat={sat} detail={detail} />
      )}

      {/* Aviation card */}
      {sat.category === 'aviation' && (
        <>
          {sat.alt_km != null && (
            <div style={s.row}><span style={s.label}>ALTITUDE</span>
              <span style={s.val}>{(sat.alt_km * 1000).toLocaleString()} m · {sat.alt_km?.toFixed(1)} km</span>
            </div>
          )}
          {sat.velocity_kms != null && (
            <div style={s.row}><span style={s.label}>SPEED</span>
              <span style={s.val}>{(sat.velocity_kms * 1000).toFixed(0)} m/s</span>
            </div>
          )}
          {sat.operator && (
            <div style={s.row}><span style={s.label}>ORIGIN</span>
              <span style={s.val}>{sat.operator}</span>
            </div>
          )}
          <div style={{ fontSize: 'var(--ui-label)', color: 'var(--txt-dim)', marginTop: 6, lineHeight: 1.5 }}>
            ADS-B via OpenSky Network
          </div>
        </>
      )}

      {sat.category !== 'groundstation' && sat.category !== 'aviation' && sat.category !== 'launches' && (
        <>
          <div style={s.row}><span style={s.label}>NORAD</span><span style={s.val}>{sat.norad_id}</span></div>
          <div style={s.row}><span style={s.label}>ALT</span><span style={s.val}>{sat.alt_km?.toFixed(0)} km</span></div>
          <div style={s.row}><span style={s.label}>VELOCITY</span><span style={s.val}>{sat.velocity_kms} km/s</span></div>
          <div style={s.row}><span style={s.label}>PERIOD</span><span style={s.val}>{sat.period_min} min</span></div>
          <div style={s.row}><span style={s.label}>INCLINATION</span><span style={s.val}>{sat.inclination?.toFixed(1)}°</span></div>
        </>
      )}
      {/* Operator/country from detail (not sat, stripped from bulk payload) */}
      {(detail?.operator || sat.operator) && (
        <div style={s.row}>
          <span style={s.label}>OPERATOR</span>
          <span style={s.val}>{detail?.operator || sat.operator}</span>
        </div>
      )}
      {(detail?.country_code || sat.country) && (
        <div style={s.row}>
          <span style={s.label}>COUNTRY</span>
          <span style={s.val}>{detail?.country_code || sat.country}</span>
        </div>
      )}

      {/* SATCAT + UCS enrichment, orbital objects only */}
      {detail && sat.category !== 'aviation' && sat.category !== 'groundstation' && (
        <>
          <hr style={s.divider} />

          {/* UCS mission context, the most human-readable section */}
          {(detail.ucs_purpose || detail.ucs_users) && (
            <>
              {detail.ucs_users && (
                <div style={s.row}>
                  <span style={s.label}>USERS</span>
                  <span style={s.val}>{detail.ucs_users}</span>
                </div>
              )}
              {detail.ucs_purpose && (
                <div style={s.row}>
                  <span style={s.label}>PURPOSE</span>
                  <span style={{ ...s.val, textAlign: 'right', maxWidth: 180, fontSize: 'var(--ui-body)' }}>
                    {detail.ucs_purpose}
                  </span>
                </div>
              )}
              {detail.ucs_detailed_purpose && detail.ucs_detailed_purpose !== detail.ucs_purpose && (
                <div style={{ fontSize: 'var(--ui-label)', color: 'var(--txt-dim)', lineHeight: 1.5,
                               marginBottom: 6, textAlign: 'right' }}>
                  {detail.ucs_detailed_purpose}
                </div>
              )}
              {detail.ucs_comments && (
                <div style={{ fontSize: 'var(--ui-label)', color: 'var(--txt-dim)', lineHeight: 1.5,
                               fontStyle: 'italic', marginBottom: 6 }}>
                  {detail.ucs_comments}
                </div>
              )}
              <hr style={s.divider} />
            </>
          )}

          {detail.cospar_id && (
            <div style={s.row}>
              <span style={s.label}>COSPAR</span>
              <span style={s.val}>{detail.cospar_id}</span>
            </div>
          )}
          {detail.launch_date && (
            <div style={s.row}>
              <span style={s.label}>LAUNCHED</span>
              <span style={s.val}>
                {new Date(detail.launch_date).toLocaleDateString('en-US', {
                  year: 'numeric', month: 'short', day: 'numeric'
                })}
              </span>
            </div>
          )}
          {detail.launch_site && (
            <div style={s.row}>
              <span style={s.label}>LAUNCH SITE</span>
              <span style={{ ...s.val, textAlign: 'right', maxWidth: 180, fontSize: 'var(--ui-body)' }}>
                {detail.launch_site}
              </span>
            </div>
          )}
          {detail.launch_vehicle && (
            <div style={s.row}>
              <span style={s.label}>ROCKET</span>
              <span style={s.val}>{detail.launch_vehicle}</span>
            </div>
          )}
          {detail.object_type && (
            <div style={s.row}>
              <span style={s.label}>OBJECT TYPE</span>
              <span style={s.val}>{detail.object_type}</span>
            </div>
          )}
          {detail.launch_mass_kg && (
            <div style={s.row}>
              <span style={s.label}>MASS</span>
              <span style={s.val}>{detail.launch_mass_kg.toLocaleString()} kg</span>
            </div>
          )}
          {detail.expected_life_yrs && (
            <div style={s.row}>
              <span style={s.label}>DESIGN LIFE</span>
              <span style={s.val}>{detail.expected_life_yrs} yrs</span>
            </div>
          )}
          {detail.contractor && (
            <div style={s.row}>
              <span style={s.label}>CONTRACTOR</span>
              <span style={{ ...s.val, textAlign: 'right', maxWidth: 180, fontSize: 'var(--ui-body)' }}>
                {detail.contractor}
              </span>
            </div>
          )}
          {detail.radar_rcs != null && (
            <div style={s.row}>
              <span style={s.label}>RADAR CROSS-SECT</span>
              <span style={s.val}>
                {detail.radar_rcs.toFixed(2)} m²
                {detail.radar_rcs_size ? ` (${detail.radar_rcs_size})` : ''}
              </span>
            </div>
          )}
          {detail.apogee_km != null && (
            <div style={s.row}>
              <span style={s.label}>APOGEE / PERIGEE</span>
              <span style={s.val}>
                {detail.apogee_km?.toLocaleString()} / {detail.perigee_km?.toLocaleString()} km
              </span>
            </div>
          )}
        </>
      )}
      {sat.reentry && (
        <>
          <hr style={s.divider} />
          <div style={{ ...s.row, color: 'var(--warn)', fontWeight: 700, fontSize: 'var(--ui-body)' }}>
            ⚠ Predicted Reentry
          </div>
          {(() => {
            const daysLeft = (new Date(sat.reentry.decay_epoch) - Date.now()) / 86400000;
            const urgency = daysLeft < 7 ? 'var(--warn)' : daysLeft < 14 ? 'var(--warn)' : 'var(--amber)';
            const corridorTier = tierFor(sat.reentry.decay_epoch, sat.reentry.uncertainty_days);
            return (
              <>
                <div style={s.row}>
                  <span style={s.label}>DATE</span>
                  <span style={{ color: urgency, fontWeight: 700 }}>
                    {new Date(sat.reentry.decay_epoch).toLocaleDateString()}
                  </span>
                </div>
                <div style={s.row}>
                  <span style={s.label}>IN</span>
                  <span style={{ color: urgency, fontWeight: 700 }}>
                    {daysLeft < 1
                      ? `${Math.floor(daysLeft * 24)}h`
                      : `${daysLeft.toFixed(1)} days`}
                  </span>
                </div>
                {sat.reentry.uncertainty_days && (
                  <div style={s.row}>
                    <span style={s.label}>UNCERTAINTY</span>
                    <span style={s.val}>±{sat.reentry.uncertainty_days.toFixed(1)} days</span>
                  </div>
                )}
                {corridorTier === 'far' && (
                  <div style={{ fontSize: 'var(--ui-label)', color: 'var(--txt-dim)', marginTop: 6, lineHeight: 1.5, fontStyle: 'italic' }}>
                    Ground-track projection unavailable, decay timing too uncertain for accurate corridor.
                  </div>
                )}
                <div style={{ fontSize: 'var(--ui-label)', color: 'var(--txt-dim)', marginTop: 6, lineHeight: 1.5 }}>
                  Exact location cannot be predicted. Object will disintegrate on reentry.
                </div>
              </>
            );
          })()}
        </>
      )}

      {/* Ground station details */}
      {sat.groundStation && (
        <>
          <hr style={s.divider} />
          <div style={s.row}><span style={s.label}>TYPE</span>
            <span style={s.val}>{sat.groundStation.type?.toUpperCase()}</span></div>
          <div style={s.row}><span style={s.label}>CAPABILITIES</span></div>
          <div style={{ fontSize: 'var(--ui-body)', color: 'var(--txt)', lineHeight: 1.6, marginTop: 4 }}>
            {sat.groundStation.capabilities}
          </div>
        </>
      )}

      {/* ISS-specific crew + telemetry */}
      {sat.category === 'station' && sat.norad_id === 25544 && (
        <ISSDetailPanel />
      )}

      {/* Next pass from observer, not shown for ground stations, aviation, or launch sites */}
      {observer && !['groundstation','aviation','launches'].includes(sat.category) && (
        <>
          <hr style={s.divider} />
          <div style={s.row}>
            <span style={s.label}>Next pass from {observer.name?.split(',')[0]}</span>
          </div>
          {passLoading && (
            <div style={{ fontSize: 'var(--ui-label)', color: 'var(--txt-dim)', padding: '6px 0' }}>
              Computing pass…
            </div>
          )}
          {!passLoading && !nextPass && (
            <div style={{ fontSize: 'var(--ui-label)', color: 'var(--txt-dim)', padding: '6px 0' }}>
              No passes in next 3 days
            </div>
          )}
          {!passLoading && nextPass && (
            <>
              {tier > 0 && (
                <div style={s.tierRow(tierColor)}>
                  <span style={{ fontSize: 'var(--ui-body)' }}>{TIER_ICONS[tier]}</span>
                  <span style={s.tierLabel(tierColor)}>{TIER_LABELS[tier]}</span>
                </div>
              )}
              <div style={s.passSection}>
                <div style={s.passItem}>
                  <span style={s.passTime}>{formatTime(nextPass.start)}</span>
                  {' → '}
                  <span style={s.passTime}>{nextPass.end ? formatTime(nextPass.end) : '?'}</span>
                  {' · '}
                  <strong style={{ color: 'var(--txt-dim)' }}>{formatCountdown(nextPass.start)}</strong>
                </div>
                <div style={s.passItem}>
                  Max <strong style={{ color: 'var(--txt)' }}>{nextPass.max_el?.toFixed(0)}°</strong>
                  {' · '}Duration <strong style={{ color: 'var(--txt)' }}>{fmtDuration(nextPass.duration_sec)}</strong>
                </div>
                {nextPass.approx_magnitude != null && (
                  <div style={s.passItem}>
                    Mag <strong style={{ color: 'var(--txt)' }}>
                      {nextPass.approx_magnitude > 0 ? '+' : ''}{nextPass.approx_magnitude}
                    </strong>
                    {nextPass.angular_velocity_degs != null && (
                      <> · <strong style={{ color: 'var(--txt)' }}>{nextPass.angular_velocity_degs}°/s</strong></>
                    )}
                  </div>
                )}
                {tier === 3 && nextPass.azimuth_start != null && (
                  <div style={s.passItem}>
                    Az <strong style={{ color: 'var(--accent)' }}>
                      {nextPass.azimuth_start}°→{nextPass.azimuth_max}°→{nextPass.azimuth_end}°
                    </strong>
                  </div>
                )}
                <div style={s.passItem}>
                  <strong style={{ color: 'var(--txt)' }}>{nextPass.darkness}</strong>
                  {' · '}Sun <strong style={{ color: 'var(--txt)' }}>
                    {nextPass.observer_sun_el > 0 ? '+' : ''}{nextPass.observer_sun_el?.toFixed(1)}°
                  </strong>
                </div>
              </div>
            </>
          )}
        </>
      )}

      <div style={s.btns}>
        <button style={s.btn(tracked)} onClick={tracked ? onUntrack : onFollow}>
          {tracked ? <><CloseIcon /> UNFOLLOW</> : '◎ FOLLOW'}
        </button>
        {sat.norad_id && !['aviation','groundstation','launches'].includes(sat.category) && (
          <ShareButton norad={sat.norad_id} name={sat.name} />
        )}
      </div>
    </>
  );

  if (isMobile && !inline) {
    return (
      <BottomSheet open={sheetOpen} onClose={handleClose} title={sat.name} maxHeight="80vh">
        <div style={{ padding: '0 16px 16px', fontFamily: 'var(--mono)', color: 'var(--txt)' }}>
          {cardContent}
        </div>
      </BottomSheet>
    );
  }

  return (
    <div style={compact ? s.cardCompact : s.card}>
      {cardContent}
    </div>
  );
}
